import { AdminApiDal } from '../adminApiDal';
import { inject, injectable } from '../../../../modules/common/di';
import { ApiResponseBase } from '../../../models/apiResponseBase';
import { GetFirmwareGroupsApiResponse } from './getFirmwareGroupsApiResponse';
import { GetFirmwareVersionsApiResponse } from './getFirmwareVersionsApiResponse';
import { UploadFirmwareVersionApiResponse } from './uploadFirmwareVersionApiResponse';
import { CreateFirmwareUpdateJobApiResponse } from './createFirmwareUpdateJobApiResponse';
import { GetFirmwareUpdateJobsApiResponse } from './getFirmwareUpdateJobsApiResponse';

/**
 * API to manage firmware groups and updates.
 * See {@link https://iotadmins.docs.apiary.io/#reference/firmware}
 */
@injectable('FirmwareApi')
export class FirmwareApi {
  @inject('AdminApiDal') protected readonly dal!: AdminApiDal;

  /**
   * Get firmware updade groups.
   * {@link https://iotadmins.docs.apiary.io/#reference/firmware/firmware-update-jobs/get-firmware-groups}
   *
   * @param {number} [organizationId] Organization ID.
   * @returns {Promise<GetFirmwareGroupsApiResponse>}
   */
  getFirmwareGroups(organizationId?: number): Promise<GetFirmwareGroupsApiResponse> {
    const params: {
      organizationId?: number,
    } = {};

    if (organizationId) {
      params.organizationId = organizationId;
    }
    return this.dal.get(`fwgroup`, {params: params});
  }

  /**
   * Update or clear the FW update group ID for a specific device instance.
   * {@link https://iotadmins.docs.apiary.io/#reference/firmware/firmware-group/update-firmware-group-for-device}
   *
   * @param params Request parameters.
   * @param {number} params.deviceId Device instance ID.
   * @param {number} [params.groupId] Firmware group ID.
   * @returns {Promise<ApiResponseBase>}
   */
  updateFirmwareGroup(params: {
    deviceId: number;
    groupId?: number;
  }): Promise<ApiResponseBase> {
    return this.dal.put(`fwgroup`, null, {
      params: params,
    });
  }

  /**
   * Upload firmware version.
   * {@link https://iotadmins.docs.apiary.io/#reference/devices/devices/upload-firmware-version}
   *
   * @param {ArrayBuffer} file File with the firmware.
   * @param params Request parameters.
   * @param {number} params.deviceType Device type.
   * @param {string} params.firmware Firmware version number.
   * @param {string} params.fileName File name.
   * @param {string} params.checkSum Hash value of the file to upload.
   * @param {string} [params.index] Firmware part index.
   * @param [contentType] The Content-Type header. 'application/octet-stream' by default.
   * @param {function} onUploadProgress upload process callback.
   * @returns {Promise<UploadFirmwareVersionApiResponse>}
   */
  uploadFirmwareVersion(
    file: ArrayBuffer,
    params: {
      deviceType: number,
      firmware: string,
      fileName: string,
      checkSum: string,
      index?: string,
    },
    contentType: string = 'application/octet-stream',
    onUploadProgress?: (progressEvent: any) => void,
  ): Promise<UploadFirmwareVersionApiResponse> {
    return this.dal.post(`fwversion`, file, {
      params: params,
      onUploadProgress: onUploadProgress,
      headers: {'Content-Type': contentType},
    })
  }

  /**
   * Get firmware versions.
   * {@link https://iotadmins.docs.apiary.io/#reference/firmware/firmware-version/get-firmware-versions}
   *
   * @param {number} deviceType Device type.
   * @returns {Promise<GetFirmwareVersionsApiResponse>}
   */
  getFirmwareVersions(deviceType: number): Promise<GetFirmwareVersionsApiResponse> {
    return this.dal.get(`fwversion`, {
      params: {
        deviceType: deviceType,
        sortCollection: 'versions',
      }
    });
  }

  /**
   * Delete specified firmware version.
   * {@link https://iotadmins.docs.apiary.io/#reference/firmware/firmware-version/delete-firmware-versions}
   *
   * @param {number} versionId Firmware version ID.
   * @returns {Promise<ApiResponseBase>}
   */
  deleteFirmwareVersion(versionId: number): Promise<ApiResponseBase> {
    return this.dal.delete(`fwversion`, {params: {
      versionId: versionId,
    }});
  }

  /**
   * Create firmware update job.
   * {@link https://iotadmins.docs.apiary.io/#reference/firmware/firmware-update-jobs/create-update-job}
   *
   * @param params Request parameters.
   * @param {number} params.versionId Firmware version ID.
   * @param {boolean} [params.forced] Force the update job.
   * @param {number} [params.groupId] Firmware group ID.
   * @returns {Promise<createFirmwareUpdateJobApiResponse>}
   */
  createFirmwareUpdateJob(params: {
    versionId: number,
    forced?: boolean,
    groupId?: number,
  }): Promise<CreateFirmwareUpdateJobApiResponse> {
    return this.dal.post(`fwjobs`, null, {params: params});
  }

  /**
   * Get firmware update jobs for the device type.
   * {@link https://iotadmins.docs.apiary.io/#reference/firmware/firmware-update-jobs/get-update-jobs}
   *
   * @param params Requeste paramaters.
   * @param {number} params.deviceType Device type.
   * @param {number} [params.groupId] Firmware update group ID.
   * @returns {Promise<GetFirmwareUpdateJobsApiResponse>}
   */
  getFirmwareUpdateJob(params: {
    deviceType: number,
    groupId?: number,
  }): Promise<GetFirmwareUpdateJobsApiResponse> {
    return this.dal.get(`fwjobs`, {params: params});
  }

  /**
   * Delete firmware update job.
   * {@link https://iotadmins.docs.apiary.io/#reference/firmware/firmware-update-jobs/delete-update-job}
   *
   * @param {number} jobId Firmware job ID to delete.
   * @returns {Promise<ApiResponseBase>}
   */
  deleteFirmwareUpdateJob(jobId: number): Promise<ApiResponseBase> {
    return this.dal.delete(`fwjobs`, {params: {
      jobId: jobId,
    }});
  }

}
